import { QK } from '@api/queryKeys';
import type {
  CreateBlockTemplateDraftFromRequestBlockInput,
  CreateBlockTemplateFromRequestBlockInput,
  OwnershipTransferInput,
  RequestBlockPatchInput,
  RequestBlockWriteInput,
  ReviewVersionInput,
  SubmitForReviewInput,
  TemplateSharesInput,
} from '@api/types/requestFlow';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/context/ToastContext';
import { blockTemplatesApi } from './blockTemplates';

type Id = string | number;

// ── Queries ───────────────────────────────────────────────────────────────────

export const useBlockTemplates = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: QK.blockTemplates(),
    queryFn: () => blockTemplatesApi.list(),
    enabled: options?.enabled ?? true,
  });

export const useBlockTemplate = (templateId?: Id, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: QK.blockTemplate(templateId as Id),
    queryFn: () => blockTemplatesApi.get(templateId as Id),
    enabled: templateId != null && (options?.enabled ?? true),
  });

export const useBlockTemplateVersionActivity = (templateId?: Id, versionId?: Id) =>
  useQuery({
    queryKey: QK.blockTemplateVersionActivity(templateId as Id, versionId as Id),
    queryFn: () => blockTemplatesApi.getVersionActivity(templateId as Id, versionId as Id),
    enabled: templateId != null && versionId != null,
  });

// ── Mutations ─────────────────────────────────────────────────────────────────

export const useCreateBlockTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RequestBlockWriteInput) => blockTemplatesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.blockTemplates() }),
  });
};

export const useCreateBlockTemplateDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: Id) => blockTemplatesApi.createDraft(templateId),
    onSuccess: (_, templateId) => {
      qc.invalidateQueries({ queryKey: QK.blockTemplate(templateId) });
      qc.invalidateQueries({ queryKey: QK.blockTemplates() });
    },
  });
};

export const useUpdateBlockTemplateVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      versionId,
      data,
    }: {
      templateId: Id;
      versionId: Id;
      data: RequestBlockPatchInput;
    }) => blockTemplatesApi.updateVersion(templateId, versionId, data),
    onSuccess: (_, { templateId }) => {
      qc.invalidateQueries({ queryKey: QK.blockTemplate(templateId) });
    },
  });
};

export const useSubmitBlockTemplateForReview = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({
      templateId,
      versionId,
      data,
    }: {
      templateId: Id;
      versionId: Id;
      data: SubmitForReviewInput;
    }) => blockTemplatesApi.submitForReview(templateId, versionId, data),
    onSuccess: (_, { templateId }) => {
      qc.invalidateQueries({ queryKey: QK.blockTemplate(templateId) });
      qc.invalidateQueries({ queryKey: QK.blockTemplates() });
    },
    onError: (error: any) => {
      const backendMessage = error?.response?.data?.message;
      showToast({
        type: 'warning',
        message: backendMessage || 'Failed to submit block template for review',
      });
    },
  });
};

export const useReviewBlockTemplateVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      versionId,
      data,
    }: {
      templateId: Id;
      versionId: Id;
      data: ReviewVersionInput;
    }) => blockTemplatesApi.reviewVersion(templateId, versionId, data),
    onSuccess: (_, { templateId }) => {
      qc.invalidateQueries({ queryKey: QK.blockTemplate(templateId) });
      qc.invalidateQueries({ queryKey: QK.blockTemplates() });
    },
  });
};

export const useDuplicateBlockTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: Id) => blockTemplatesApi.duplicate(templateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.blockTemplates() }),
  });
};

export const useDeleteBlockTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: Id) => blockTemplatesApi.delete(templateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.blockTemplates() }),
  });
};

export const useReplaceBlockTemplateShares = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: Id; data: TemplateSharesInput }) =>
      blockTemplatesApi.replaceShares(templateId, data),
    onSuccess: (_, { templateId }) => {
      qc.invalidateQueries({ queryKey: QK.blockTemplate(templateId) });
    },
  });
};

export const useTransferBlockTemplateOwnership = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: Id; data: OwnershipTransferInput }) =>
      blockTemplatesApi.transferOwnership(templateId, data),
    onSuccess: (_, { templateId }) => {
      qc.invalidateQueries({ queryKey: QK.blockTemplate(templateId) });
      qc.invalidateQueries({ queryKey: QK.blockTemplates() });
    },
  });
};

export const useCreateBlockTemplateFromBlock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBlockTemplateFromRequestBlockInput) =>
      blockTemplatesApi.createFromBlock(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.blockTemplates() }),
  });
};

export const useCreateBlockTemplateDraftFromBlock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBlockTemplateDraftFromRequestBlockInput) =>
      blockTemplatesApi.createDraftFromBlock(data),
    onSuccess: (envelope) => {
      qc.invalidateQueries({ queryKey: QK.blockTemplate(envelope.template.id) });
      qc.invalidateQueries({ queryKey: QK.blockTemplates() });
    },
  });
};
